const API_BASE =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production'
    ? 'https://christreignsmedia-api-b91e81f2bbea.herokuapp.com'
    : 'http://localhost:3001');

const TOKEN_KEY = 'crm_admin_token';

// ─── Auth helpers ────────────────────────────────────────────────────────────

export function saveToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

export function isLoggedIn(): boolean {
  return !!getToken();
}

function authHeaders(): HeadersInit {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// ─── Auth ────────────────────────────────────────────────────────────────────

export async function login(email: string, password: string): Promise<string> {
  const res = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Invalid email or password');
  }
  const data = await res.json();
  return data.accessToken as string;
}

export async function register(email: string, password: string): Promise<void> {
  const res = await fetch(`${API_BASE}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Registration failed');
  }
}

// ─── Articles ────────────────────────────────────────────────────────────────

export interface ArticlePayload {
  title: string;
  subtitle?: string;
  content?: string;
  url: string;
  publishedAt?: string;
  language: 'en' | 'es';
  imageUrl?: string;
}

export async function fetchAllArticles() {
  const res = await fetch(`${API_BASE}/articles`);
  if (!res.ok) throw new Error('Failed to fetch articles');
  const data = await res.json();
  return data.articles as any[];
}

export async function createArticle(payload: ArticlePayload) {
  const res = await fetch(`${API_BASE}/articles`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to create article');
  }
  return res.json();
}

export async function updateArticle(id: string, payload: Partial<ArticlePayload>) {
  const res = await fetch(`${API_BASE}/articles/${id}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to update article');
  }
  return res.json();
}

export async function deleteArticle(id: string) {
  const res = await fetch(`${API_BASE}/articles/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to delete article');
  }
  return res.json();
}

// ─── Books ───────────────────────────────────────────────────────────────────

export interface Book {
  _id: string;
  title: string;
  author: string;
  imageUrl: string;
  order: number;
  isActive: boolean;
  buyUrl?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface BookPayload {
  title: string;
  author: string;
  imageUrl?: string;
  order?: number;
  isActive?: boolean;
  buyUrl?: string;
}

export async function fetchBooks(): Promise<Book[]> {
  const res = await fetch(`${API_BASE}/books`);
  if (!res.ok) throw new Error('Failed to fetch books');
  const data = await res.json();
  return data.books as Book[];
}

export async function fetchBooksAdmin(): Promise<Book[]> {
  const res = await fetch(`${API_BASE}/books/admin/list`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to fetch admin book list');
  const data = await res.json();
  return data.books as Book[];
}

export async function createBook(payload: BookPayload): Promise<Book> {
  const res = await fetch(`${API_BASE}/books`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to create book');
  }
  const data = await res.json();
  return data.book as Book;
}

export async function updateBook(id: string, payload: Partial<BookPayload>): Promise<Book> {
  const res = await fetch(`${API_BASE}/books/${id}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to update book');
  }
  const data = await res.json();
  return data.book as Book;
}

export async function deleteBook(id: string): Promise<void> {
  const res = await fetch(`${API_BASE}/books/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to delete book');
  }
}

